import React, { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import axios from "axios"
import * as XLSX from 'xlsx';

const BudgetAnalysis = () => {
  const COMPANY_CODE = localStorage?.getItem("companyCode") || "-";
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedAccountType, setSelectedAccountType] = useState("");
  const [selectedDeptNo, setSelectedDeptNo] = useState("-");

  const [years, setYears] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [budgetData, setBudgetData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const fetchDepartments = async () => {
    try {
      let company_code_from_local_storage = localStorage.getItem("companyCode")
        ? localStorage.getItem("companyCode")
        : "-";

      let apiPaths_from_local_storage = localStorage.getItem("apiPaths")
        ? JSON.parse(localStorage.getItem("apiPaths"))
        : "-";

      const apiBaseURL = apiPaths_from_local_storage[0]?.API_CONFIG;

      const apiURL = `${apiBaseURL}/api/CpaysCount/${COMPANY_CODE}/DEPTLIST/-/-`;
      const response = await axios.get(apiURL);
      const data = response.data;
      // console.log("Dept List:", data);
      setDepartments(data);
    } catch (error) {
      console.error("Error while fetching Dept list!", error);
    }
  };

  const initialSetup = ()=>{
    const currentYear = new Date().getFullYear();
    const yearOptions = [];
    for (let i = 0; i <= 50; i++) {
      yearOptions.push(currentYear + i);
    }
    setYears(yearOptions);
    setSelectedYear(yearOptions[0])

    setAccountTypes(["EXPENSE", "INCOME", "ASSETS", "LIABILITIES", "EQUITIES"]);
    setSelectedAccountType("EXPENSE")
  }
  useEffect(() => {
   
    initialSetup()
    fetchDepartments();
  }, []);

  useEffect(() => {
    if (selectedYear && selectedAccountType ) {
      fetchBudgetData();
    }
  }, [selectedYear, selectedAccountType, selectedDeptNo]);

  const fetchBudgetData = async () => {
    setLoading(true);
    setError(null);

    try {
      let API_PATHS = localStorage.getItem("apiPaths")
        ? JSON.parse(localStorage.getItem("apiPaths"))
        : "-";

      const BASE_URL =
        API_PATHS[0]?.API_CONFIG || "https://cubixweberp.com:213";

      const response = await fetch(
        `${BASE_URL}/api/crmbudjetanalysis/${COMPANY_CODE}/${selectedYear}/${selectedAccountType}/${selectedDeptNo}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch budget data");
      }

      const data = await response.json();
      setBudgetData(data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching budget data:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return "-";
    const isNegative = value < 0;
    const formatted = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
    return  formatted;
  };

  const calculateMonthVariance = (budget, actual) => {
    const b = budget || 0;
    const a = actual || 0;

    console.log("a - b ",a , b,"--", a - b)
    return a - b;
  };

  const calculateTotals = (account) => {
    let totalBudget = 0;
    let totalActual = 0;

    months.forEach((month) => {
      totalBudget += account[`${month}_BUDGET`] || 0;
      totalActual += account[`${month}_ACTUAL`] || 0;
    });

    return {
      totalBudget,
      totalActual,
      variance: totalActual - totalBudget,
    };
  };

  const calculateGrandTotals = () => {
    let grandBudget = 0;
    let grandActual = 0;

    budgetData.forEach((account) => {
      const totals = calculateTotals(account);
      grandBudget += totals.totalBudget;
      grandActual += totals.totalActual;
    });

    return {
      grandBudget,
      grandActual,
      grandVariance: grandActual - grandBudget,
    };
  };

  const handleReset = () => {
    initialSetup()
    setSelectedDeptNo("-");
    setBudgetData([]);
    setError(null);
  };

  const grandTotals = budgetData.length > 0 ? calculateGrandTotals() : null;

  const exportToExcel = () => {
    // Create worksheet data
    const wsData = [
      ['Budget  Analysis'],
      [`Year: ${selectedYear}`, `Type: ${selectedAccountType}`, `Department: ${selectedDeptNo}`],
      [],
      [`Total Budget: ${ formatCurrency(grandTotals.grandBudget)}`, `Total Actual: ${formatCurrency(grandTotals.grandActual)}`, `Total Variance: ${formatCurrency(grandTotals.grandVariance)}`],
      [],
      ['Account', 'Description', 'Dept', 'Type', ...months.flatMap(m => [`${m} Budget`, `${m} Actual`, `${m} Variance`]), 'Total Budget', 'Total Actual', 'Total Variance']
    ];
  
    budgetData.forEach(account => {
      const totals = calculateTotals(account);
      const row = [
        account.account,
        account.accdesc,
        account.deptno || '',
        account.Type || '',
        ...months.flatMap(month => [
          account[`${month}_BUDGET`] || 0,
          account[`${month}_ACTUAL`] || 0,
          calculateMonthVariance(account[`${month}_BUDGET`], account[`${month}_ACTUAL`])
        ]),
        totals.totalBudget,
        totals.totalActual,
        totals.variance
      ];
      wsData.push(row);
    });
  
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, 'Budget Analysis');
    XLSX.writeFile(wb, `Budget_Analysis_${selectedYear}.xlsx`);
  };

  return (
    <div className="max-w-full mx-auto p-6 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">
          Budget Analysis
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Year
            </label>
            <div className="relative">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">Select Year</option>
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <ChevronDown
                className="absolute right-3 top-3 text-gray-400 pointer-events-none"
                size={20}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type
            </label>
            <div className="relative">
              <select
                value={selectedAccountType}
                onChange={(e) => setSelectedAccountType(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                disabled={!selectedYear}
              >
                <option value="">Select Type</option>
                {accountTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>
              <ChevronDown
                className="absolute right-3 top-3 text-gray-400 pointer-events-none"
                size={20}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Department
            </label>
            <div className="relative">
              <select
                value={selectedDeptNo}
                onChange={(e) => setSelectedDeptNo(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                disabled={!selectedAccountType}
              >
                <option value="-">Select Department</option>
                {departments.map((dept) => (
                  <option key={dept.deptno} value={dept.deptno}>
                    <span>{dept.deptno} - {dept.deptnodesc} </span>
                  </option>
                ))}
              </select>
              <ChevronDown
                className="absolute right-3 top-3 text-gray-400 pointer-events-none"
                size={20}
              />
            </div>
          </div>
        </div>

        {(selectedYear || selectedAccountType || selectedDeptNo) && (
          <button
            onClick={handleReset}
            className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Reset Filters
          </button>
        )}
      </div>

      {loading && (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading budget data...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800 font-medium">Error: {error}</p>
        </div>
      )}

      {!loading && budgetData.length > 0 && grandTotals && (
        <>
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4" style={{display:"flex",alignItems:"center", justifyContent:"space-between"}}>
              Summary - {selectedYear}

              <button
                onClick={exportToExcel}
                className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
              >
                
                Export to Excel
              </button>

            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-1">Total Budget</div>
                <div className="text-2xl font-bold text-blue-600">
                  {formatCurrency(grandTotals.grandBudget)}
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-1">Total Actual</div>
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(grandTotals.grandActual)}
                </div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-1">Total Variance</div>
                <div
                  className={`text-2xl font-bold ${
                    grandTotals.grandVariance < 0
                      ? "text-red-600"
                      : "text-green-600"
                  }`}
                >
                  {formatCurrency(grandTotals.grandVariance)}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="px-3 py-3 text-left font-semibold border-b border-r">
                      Account
                    </th>
                    <th className="px-3 py-3 text-left font-semibold border-b border-r">
                      Description
                    </th>
                    <th className="px-3 py-3 text-left font-semibold border-b border-r">
                      Dept
                    </th>
                    <th className="px-3 py-3 text-left font-semibold border-b border-r">
                      Costcode
                    </th>
                    <th className="px-3 py-3 text-left font-semibold border-b border-r">
                      Type
                    </th>
                    {months.map((month) => (
                      <th
                        key={month}
                        className="px-2 py-3 text-center font-semibold border-b border-r"
                        colSpan="3"
                      >
                        {month}
                      </th>
                    ))}
                    <th
                      className="px-3 py-3 text-center font-semibold border-b bg-gray-200"
                      colSpan="3"
                    >
                      Total
                    </th>
                  </tr>
                  <tr className="bg-gray-50">
                    <th className="border-b border-r"></th>
                    <th className="border-b border-r"></th>
                    <th className="border-b border-r"></th>
                    <th className="border-b border-r"></th>
                    <th className="border-b border-r"></th>
                    {months.map((month) => (
                      <React.Fragment key={month}>
                        <th className="px-2 py-2 text-xs text-center border-b border-r">
                          Budget
                        </th>
                        <th className="px-2 py-2 text-xs text-center border-b border-r">
                          Actual
                        </th>
                        <th className="px-2 py-2 text-xs text-center border-b border-r">
                          Var
                        </th>
                      </React.Fragment>
                    ))}
                    <th className="px-2 py-2 text-xs text-center border-b border-r bg-gray-200">
                      Budget
                    </th>
                    <th className="px-2 py-2 text-xs text-center border-b border-r bg-gray-200">
                      Actual
                    </th>
                    <th className="px-2 py-2 text-xs text-center border-b bg-gray-200">
                      Variance
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {budgetData.map((account, index) => {
                    const totals = calculateTotals(account);
                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-3 py-2 border-b border-r font-mono text-xs">
                          {account.account}
                        </td>
                        <td className="px-3 py-2 border-b border-r text-xs">
                          {account.accdesc}
                        </td>
                        <td className="px-3 py-2 border-b border-r text-xs">
                          {account.deptno}
                        </td>
                        <td className="px-3 py-2 border-b border-r text-xs">
                          {account.Costcode}
                        </td>
                        <td className="px-3 py-2 border-b border-r text-xs capitalize">
                          {account.Type || "-"}
                        </td>
                        {months.map((month) => {
                          const budget = account[`${month}_BUDGET`];
                          const actual = account[`${month}_ACTUAL`];
                          const variance = account[`${month}_VARIANCE`];
                          return (
                            <React.Fragment key={month}>
                              <td className="px-2 py-2 text-right border-b border-r text-xs">
                                {budget !== null && budget !== undefined
                                  ? formatCurrency(budget)
                                  : "-"}
                              </td>
                              <td className="px-2 py-2 text-right border-b border-r text-xs">
                                {actual !== null && actual !== undefined
                                  ? formatCurrency(actual)
                                  : "-"}
                              </td>
                              <td
                                className={`px-2 py-2 text-right border-b border-r text-xs font-medium ${
                                  variance < 0
                                    ? "text-red-600"
                                    : variance > 0
                                    ? "text-green-600"
                                    : ""
                                }`}
                              >
                                {budget !== null || actual !== null
                                  ? formatCurrency(variance)
                                  : "-"}
                              </td>
                            </React.Fragment>
                          );
                        })}
                        <td className="px-2 py-2 text-right border-b border-r bg-gray-50 font-semibold text-xs">
                          {formatCurrency(totals.totalBudget)}
                        </td>
                        <td className="px-2 py-2 text-right border-b border-r bg-gray-50 font-semibold text-xs">
                          {formatCurrency(totals.totalActual)}
                        </td>
                        <td
                          className={`px-2 py-2 text-right border-b bg-gray-50 font-semibold text-xs ${
                            totals.variance < 0
                              ? "text-red-600" :
                               totals.variance === 0  ? "" : "text-green-600"
                          }`}
                        >
                          {formatCurrency(totals.variance)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!loading &&
        budgetData.length === 0 &&
        !error &&
        (selectedYear || selectedAccountType || selectedDeptNo) && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <p className="text-gray-500">
              {!selectedYear && "Please select a year to begin"}
              {selectedYear &&
                !selectedAccountType &&
                "Please select an account type"}
              {selectedYear &&
                selectedAccountType &&
                !selectedDeptNo &&
                "Please select a department"}
            </p>
          </div>
        )}
    </div>
  );
};

export default BudgetAnalysis;
